import { useMemo } from "react";

import {
  useListAvailableRoles,
  useListAvailableTechnicalInstructions,
  useListAvailableTemplatedInstructions,
} from "@api/rest/types-and-hooks";
import {
  type KeyedCatalogs,
  type SuggestionDraft,
  SuggestionField,
  needsCatalog,
} from "@pages/Evaluations/promptSuggestionFields";

/** The keyed-field catalogs the drafts' diffs resolve against, loaded only
 *  for the fields a draft changes. */
export const useSuggestionCatalogs = (
  drafts: SuggestionDraft[],
): { catalogs: KeyedCatalogs; catalogsPending: boolean } => {
  const { data: rolesResp, isLoading: rolesLoading } = useListAvailableRoles({
    query: { enabled: needsCatalog(drafts, SuggestionField.ROLE) },
  });
  const { data: templatedResp, isLoading: templatedLoading } = useListAvailableTemplatedInstructions({
    query: { enabled: needsCatalog(drafts, SuggestionField.TEMPLATED_INSTRUCTIONS) },
  });
  const { data: technicalResp, isLoading: technicalLoading } = useListAvailableTechnicalInstructions({
    query: { enabled: needsCatalog(drafts, SuggestionField.TECHNICAL_INSTRUCTIONS) },
  });
  const catalogs = useMemo<KeyedCatalogs>(
    () => ({
      [SuggestionField.ROLE]: rolesResp?.status === 200 ? rolesResp.data.items : undefined,
      [SuggestionField.TEMPLATED_INSTRUCTIONS]: templatedResp?.status === 200 ? templatedResp.data.items : undefined,
      [SuggestionField.TECHNICAL_INSTRUCTIONS]: technicalResp?.status === 200 ? technicalResp.data.items : undefined,
    }),
    [rolesResp, templatedResp, technicalResp],
  );
  return { catalogs, catalogsPending: rolesLoading || templatedLoading || technicalLoading };
};
